import { FC, useState } from 'react';
import { Container } from '@/components/ui/Container';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { CONTACT_INFO, PRICING_PLANS } from '@/utils/constants';
import { contactApi, ApiError } from '@/services/api';
import { ContactForm } from '@/types';
import wechatQrCode from '@/assets/1.jpg';

export const ContactPage: FC = () => {
  const [formData, setFormData] = useState<ContactForm>({
    name: '',
    phone: '',
    type: 'individual',
    plan: '',
    scenario: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string>('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    try {
      // 尝试提交到后端API
      await contactApi.submit(formData);
      setSubmitted(true);
    } catch (err) {
      // 如果后端API不可用，降级为本地处理
      if (err instanceof ApiError && err.code === 0) {
        console.log('后端API不可用，使用本地处理模式');
        // 模拟提交成功
        await new Promise(resolve => setTimeout(resolve, 1000));
        setSubmitted(true);
      } else {
        setError(err instanceof Error ? err.message : '提交失败，请稍后重试');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  if (submitted) {
    return (
      <div className="py-20">
        <Container>
          <div className="max-w-2xl mx-auto text-center">
            <div className="text-6xl mb-6">✅</div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">提交成功！</h1>
            <p className="text-xl text-gray-600 mb-8">
              感谢您的咨询！我们的客服团队会在24小时内主动联系您，请保持电话畅通。
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="font-semibold text-blue-900 mb-2">接下来的流程：</h3>
              <ul className="text-blue-800 text-left space-y-2">
                <li>1. 客服确认您的需求和合适的套餐方案</li>
                <li>2. 提供详细的功能演示和答疑</li>
                <li>3. 协助完成购买和支付流程</li>
                <li>4. 提供API密钥和安装指导</li>
                <li>5. 后续技术支持和服务保障</li>
              </ul>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <>
      {/* Hero Section */}
      <section className="py-12 bg-gradient-to-br from-gray-50 to-blue-50">
        <Container>
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              联系我们
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              专业客服团队为您提供购买咨询和技术支持服务
            </p>
          </div>
        </Container>
      </section>

      <Container>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Information */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-8">联系方式</h2>
            
            <div className="space-y-6">
              {/* <Card>
                <div className="flex items-start space-x-4">
                  <div className="text-3xl">📞</div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">客服热线</h3>
                    <p className="text-2xl font-bold text-primary-600 mb-1">{CONTACT_INFO.phone}</p>
                    <p className="text-gray-600 text-sm">{CONTACT_INFO.workingHours}</p>
                    <p className="text-gray-600 text-sm">专业客服为您提供购买咨询和技术支持</p>
                  </div>
                </div>
              </Card> */}

              <Card>
                <div className="flex items-start space-x-4">
                  <div className="text-3xl">💬</div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">微信客服</h3>
                    <p className="text-gray-900 font-medium mb-1">扫码添加专属客服微信</p>
                    <div className="w-32 h-32 bg-gray-200 rounded-lg overflow-hidden mb-2">
                      <img 
                        src={wechatQrCode} 
                        alt="微信客服二维码" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <p className="text-gray-600 text-sm">7×24小时在线，快速响应您的需求</p>
                  </div>
                </div>
              </Card>

              <Card>
                <div className="flex items-start space-x-4">
                  <div className="text-3xl">📧</div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">商务邮箱</h3>
                    <p className="text-primary-600 font-medium mb-1">{CONTACT_INFO.email}</p>
                    <p className="text-gray-600 text-sm">商务合作和企业客户专线</p>
                  </div>
                </div>
              </Card>

              <Card>
                <div className="flex items-start space-x-4">
                  <div className="text-3xl">🏢</div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">公司地址</h3>
                    <p className="text-gray-900 mb-1">{CONTACT_INFO.address}</p>
                    <p className="text-gray-600 text-sm">欢迎预约实地考察和面对面沟通</p>
                  </div>
                </div>
              </Card>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-8">快速咨询</h2>
            
            <Card>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    姓名 *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="请输入您的姓名"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    联系电话 *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="请输入您的手机号"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    客户类型 *
                  </label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="individual">个人开发者</option>
                    <option value="enterprise">企业客户</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    意向套餐
                  </label>
                  <select
                    name="plan"
                    value={formData.plan}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">请选择套餐</option>
                    {PRICING_PLANS.map(plan => (
                      <option key={plan.id} value={plan.name}>
                        {plan.name} - ¥{plan.price}/月
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    使用场景
                  </label>
                  <textarea
                    name="scenario"
                    rows={4}
                    value={formData.scenario}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="请简单描述您的使用场景和需求（可选）"
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex">
                      <div className="text-red-400 mr-3">
                        ⚠️
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-red-800">
                          提交失败
                        </h3>
                        <p className="mt-1 text-sm text-red-700">
                          {error}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? '提交中...' : '提交咨询'}
                </Button>
              </form>

              {/* <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-600 text-center">
                  提交后我们将在24小时内联系您<br/>
                  或直接拨打客服热线 <span className="font-semibold text-primary-600">{CONTACT_INFO.phone}</span> 获得即时服务
                </p>
              </div> */}
            </Card>
          </div>
        </div>
      </Container>
    </>
  );
};